import React, { useEffect, useRef, useState } from 'react';
import { content, colors } from '../data/content';
import { Leaf, Award, Heart, Fish } from 'lucide-react';

const About = ({ language }) => {
  const currentContent = content[language].about;
  const isRTL = language === 'ar';
  const [visibleFeatures, setVisibleFeatures] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            currentContent.features.forEach((_, index) => {
              setTimeout(() => {
                setVisibleFeatures((prev) => [...prev, index]);
              }, index * 150);
            });
          }
        });
      },
      { threshold: 0.2 }
    );

    const currentSection = sectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, [currentContent.features]);

  const iconMap = {
    leaf: Leaf,
    award: Award,
    heart: Heart,
    fish: Fish,
  };

  return (
    <section 
      id="about" 
      ref={sectionRef}
      style={{
        background: `linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)`,
        padding: 'clamp(60px, 10vw, 120px) clamp(20px, 4vw, 40px)',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        inset: 0,
        opacity: 0.03,
        backgroundImage: `radial-gradient(circle at 2px 2px, ${colors.secondary} 1px, transparent 0)`,
        backgroundSize: '40px 40px'
      }}></div>

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '0 clamp(20px, 4vw, 40px)',
        position: 'relative',
        zIndex: 10
      }}>
        {/* Section Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: 'clamp(48px, 8vw, 80px)',
          animation: 'fadeInUp 0.8s ease-out'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 20px',
            background: `linear-gradient(135deg, ${colors.primary}10 0%, ${colors.secondary}10 100%)`,
            borderRadius: '50px',
            marginBottom: '20px',
            border: `1px solid ${colors.primary}20`
          }}>
            <Award size={18} style={{ color: colors.primary }} />
            <span style={{
              fontSize: '14px',
              fontWeight: 600,
              color: colors.primary,
              fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif'
            }}>
              {isRTL ? 'لماذا نحن' : 'Why Choose Us'}
            </span>
          </div>
          
          <h2 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: 800,
            color: colors.secondary,
            textAlign: 'center',
            marginBottom: 'clamp(16px, 3vw, 24px)',
            fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
            background: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.primary} 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            lineHeight: isRTL ? 1.65 : 1.2
          }}>
            {currentContent.title}
          </h2>
        </div>

        {/* Features Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
          gap: 'clamp(16px, 4vw, 32px)'
        }}>
          {currentContent.features.map((feature, index) => {
            const IconComponent = iconMap[feature.icon] || Award;
            const iconColor = feature.color || colors.secondary;
            const isVisible = visibleFeatures.includes(index);
            
            return (
              <div
                key={index}
                style={{
                  background: 'white',
                  borderRadius: '20px',
                  padding: 'clamp(20px, 4vw, 28px)',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  textAlign: 'center',
                  position: 'relative',
                  overflow: 'hidden',
                  opacity: isVisible ? 1 : 0,
                  transform: isVisible ? 'translateY(0)' : 'translateY(30px)',
                  animation: isVisible ? 'fadeInUp 0.6s ease-out' : 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-12px) scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 20px 40px rgba(0, 0, 0, 0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 4px 20px rgba(0, 0, 0, 0.08)';
                }}
              >
                {/* Background Gradient */}
                <div style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  height: '4px',
                  background: `linear-gradient(90deg, ${iconColor} 0%, ${feature.color || colors.primary} 100%)`,
                  opacity: 0,
                  transition: 'opacity 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '0';
                }}
                ></div>

                {/* Icon Container */}
                <div style={{
                  width: 'clamp(56px, 8vw, 70px)',
                  height: 'clamp(56px, 8vw, 70px)',
                  borderRadius: '16px',
                  background: `linear-gradient(135deg, ${iconColor}15 0%, ${iconColor}05 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto clamp(16px, 3vw, 20px)',
                  position: 'relative',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1) rotate(5deg)';
                  e.currentTarget.style.background = `linear-gradient(135deg, ${iconColor}25 0%, ${iconColor}15 100%)`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                  e.currentTarget.style.background = `linear-gradient(135deg, ${iconColor}15 0%, ${iconColor}05 100%)`;
                }}
                >
                  <IconComponent 
                    size={typeof window !== 'undefined' && window.innerWidth < 768 ? 28 : 32} 
                    style={{ 
                      color: iconColor,
                      transition: 'all 0.3s ease'
                    }} 
                  />
                  
                  {/* Decorative Circle */}
                  <div style={{
                    position: 'absolute',
                    top: '-10px',
                    [isRTL ? 'left' : 'right']: '-10px',
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    background: `${iconColor}20`,
                    filter: 'blur(8px)',
                    animation: 'pulse 2s ease-in-out infinite'
                  }}></div>
                </div>

                <h3 style={{
                  fontSize: 'clamp(1rem, 2.5vw, 1.2rem)',
                  fontWeight: 700,
                  marginBottom: 'clamp(8px, 1.5vw, 12px)',
                  color: colors.secondary,
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  transition: 'color 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = iconColor;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = colors.secondary;
                }}
                >
                  {feature.title}
                </h3>

                <p style={{
                  color: colors.text,
                  fontSize: 'clamp(0.8rem, 1.8vw, 0.9rem)',
                  lineHeight: isRTL ? 1.7 : 1.6,
                  fontFamily: isRTL ? 'Cairo, sans-serif' : 'Poppins, sans-serif',
                  margin: 0
                }}>
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default About;
